import { type Clusterzuordnung, clusterzuordnung, type ExpertePlanung } from '../../../dtos';
import { formatDate, getExperteName } from '../../../utils';
import { type Content, content } from './TerminExpertepoolCard.content';

export const printName = (expertePlanung: ExpertePlanung): string => {
  if (expertePlanung.experte === null) {
    return content.noInfoAvailable;
  }

  return getExperteName(expertePlanung.experte);
};

type ClusterLetter = 'A' | 'B' | 'C' | 'D';
type ClusterDisplay = ClusterLetter | Content['noInfoAvailable'];

const clusterzuordnungAbbreviation = {
  HAUPTTRAINER: 'A',
  NEBENTRAINER: 'B',
  ERSATZTRAINER: 'C',
  WEITERER_TRAINER: 'D',
} as const satisfies Record<keyof typeof clusterzuordnung, ClusterLetter>;

const getClusterzuordnungAbbreviation = (givenClusterzuordnung: Clusterzuordnung): ClusterLetter => {
  switch (givenClusterzuordnung) {
    case clusterzuordnung.HAUPTTRAINER:
      return clusterzuordnungAbbreviation.HAUPTTRAINER;
    case clusterzuordnung.NEBENTRAINER:
      return clusterzuordnungAbbreviation.NEBENTRAINER;
    case clusterzuordnung.ERSATZTRAINER:
      return clusterzuordnungAbbreviation.ERSATZTRAINER;
    case clusterzuordnung.WEITERER_TRAINER:
      return clusterzuordnungAbbreviation.WEITERER_TRAINER;
    default: {
      const exhaustiveCheck: never = givenClusterzuordnung;
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Could not find abbreviation for clusterzuordnung ${exhaustiveCheck}`);
    }
  }
};

export const printCluster = (passedClusterzuordnung: Clusterzuordnung | null): ClusterDisplay => {
  if (!passedClusterzuordnung) {
    return content.noInfoAvailable;
  }

  return getClusterzuordnungAbbreviation(passedClusterzuordnung);
};

const today = new Date();
export const thisYear = today.getFullYear();
export const nextYear = today.getFullYear() + 1;

// eslint-disable-next-line @typescript-eslint/restrict-plus-operands
export const printAuslastungAktuellesJahrLabel = (jahr: number): string => content.columnLabels.auslastung + ' ' + jahr;

export const printAuslastungForJahr = (jahr: number, expertePlanung: ExpertePlanung): string => {
  const experteAuslastungen = expertePlanung.experte?.experteAuslastungen;

  const experteAuslastung = experteAuslastungen?.find((item) => item.jahr === jahr);

  if (experteAuslastung === undefined) {
    return content.noInfoAvailable;
  }

  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  return Math.round(experteAuslastung.summeTage) + '/' + Math.round(experteAuslastung.schwelleRot);
};

export const clusterField = 'cluster';

export const printGueltigkeitszeitraeume = (gueltigkeitszeitraeume: { start: Date; end: Date }[] | null): string => {
  if (gueltigkeitszeitraeume === null || gueltigkeitszeitraeume.length === 0) {
    return content.noInfoAvailable;
  }

  return gueltigkeitszeitraeume
    .map((gueltigkeit) => `${formatDate(gueltigkeit.start, true)} - ${formatDate(gueltigkeit.end, true)}`)
    .join(', ');
};
